/**
 * Reusable metrics card component for admin dashboard
 */

import React from 'react';
import { Card } from '../ui/Card';

interface MetricsCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  trend?: {
    value: number;
    isPositive: boolean;
    label: string;
  };
  icon?: React.ReactNode;
  className?: string;
  loading?: boolean;
}

export function MetricsCard({
  title,
  value,
  subtitle,
  trend,
  icon,
  className = '',
  loading = false
}: MetricsCardProps) {
  if (loading) {
    return (
      <Card className={`p-6 ${className}`}>
        <div className="animate-pulse">
          <div className="flex items-center justify-between mb-2">
            <div className="h-4 bg-gray-700 rounded w-24"></div>
            <div className="h-6 w-6 bg-gray-700 rounded"></div>
          </div>
          <div className="h-8 bg-gray-700 rounded w-16 mb-2"></div>
          <div className="h-3 bg-gray-700 rounded w-20"></div>
        </div>
      </Card>
    );
  }

  return (
    <Card className={`p-6 ${className}`}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider">
          {title}
        </h3>
        {icon && (
          <div className="text-gray-400">
            {icon}
          </div>
        )}
      </div>
      
      <div className="flex items-end justify-between">
        <div>
          <div className="text-2xl font-bold text-white mb-1">
            {typeof value === 'number' ? value.toLocaleString() : value}
          </div>
          {subtitle && (
            <div className="text-sm text-gray-400">
              {subtitle}
            </div>
          )}
        </div>
        
        {trend && (
          <div className={`flex items-center text-sm ${
            trend.isPositive ? 'text-green-400' : 'text-red-400'
          }`}>
            <span className="mr-1">
              {trend.isPositive ? '↗' : '↘'}
            </span>
            <span className="font-medium">
              {Math.abs(trend.value)}%
            </span>
            <span className="text-gray-400 ml-1">
              {trend.label}
            </span>
          </div>
        )}
      </div>
    </Card>
  );
}

// Specialized metric cards
interface UserMetricsProps {
  stats: {
    admin_users: number;
    instructor_users: number;
    learner_users: number;
    total_users: number;
    active_users: number;
  };
  loading?: boolean;
}

export function UserMetricsCard({ stats, loading }: UserMetricsProps) {
  if (loading) {
    return <MetricsCard title="Users" value="--" loading={true} />;
  }

  return (
    <Card className="p-6">
      <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-4">
        Users
      </h3>
      
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-2xl font-bold text-white">
            {stats.total_users.toLocaleString()}
          </span>
          <span className="text-sm text-gray-400">Total</span>
        </div>
        
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-400">Active:</span>
            <span className="text-green-400 font-medium">{stats.active_users}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Learners:</span>
            <span className="text-white">{stats.learner_users}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Instructors:</span>
            <span className="text-blue-400">{stats.instructor_users}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Admins:</span>
            <span className="text-purple-400">{stats.admin_users}</span>
          </div>
        </div>
      </div>
    </Card>
  );
}

interface RunMetricsProps {
  stats: {
    pending_runs: number;
    provisioning_runs: number;
    active_runs: number;
    destroying_runs: number;
    destroyed_runs: number;
    failed_runs: number;
    total_runs: number;
  };
  loading?: boolean;
}

export function RunMetricsCard({ stats, loading }: RunMetricsProps) {
  if (loading) {
    return <MetricsCard title="Lab Instances" value="--" loading={true} />;
  }

  const activeRuns = stats.pending_runs + stats.provisioning_runs + stats.active_runs;
  const successRate = stats.total_runs > 0 
    ? ((stats.destroyed_runs / stats.total_runs) * 100).toFixed(1)
    : '0.0';

  return (
    <Card className="p-6">
      <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-4">
        Lab Instances
      </h3>
      
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-2xl font-bold text-white">
            {stats.total_runs.toLocaleString()}
          </span>
          <span className="text-sm text-gray-400">Total</span>
        </div>
        
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-400">Active:</span>
            <span className={`font-medium ${
              activeRuns > 0 ? 'text-green-400' : 'text-gray-400'
            }`}>
              {activeRuns}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Completed:</span>
            <span className="text-white">{stats.destroyed_runs}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Success Rate:</span>
            <span className="text-green-400 font-medium">{successRate}%</span>
          </div>
        </div>
      </div>
    </Card>
  );
}
